import React from 'react';
import { SystemPage } from '@parsina/framework';

import { useTranslation } from '../i18n';

/**
 * A whole-page system state, with its copy resolved from the catalogue.
 *
 * The kit's `SystemPage` carries no translations (DESIGN.md rule 7) and takes its
 * strings as props; this is the thin app-side wrapper that supplies them, so no
 * screen assembles a `SystemPage` out of literals of its own. One shape for every
 * whole-page fault is deliberate: these are the moments a user is already unsure
 * whether the app works, and six layouts for six faults read as six kinds of
 * broken.
 *
 * Actions are passed through as DESCRIPTORS, not as rendered buttons: the kit
 * already draws them, and it makes the first one primary unless told otherwise —
 * a system page with two equally-weighted buttons asks the user to decide
 * something they have no basis to decide.
 *
 * Props:
 *   tone      colours the icon disc ONLY (a red wash behind a 404 would make a
 *             mistyped address look like an incident)
 *   titleKey / adviceKey  catalogue keys
 *   values    interpolation values for both
 *   code      the watermark — '404', '403'
 *   icon      a lucide node
 *   detail    LTR/monospace technical line (an SSO host, an id)
 *   actions   [{ key, label, onClick, href, variant: 'primary'|'secondary' }]
 */
export default function SystemState({
  tone = 'neutral',
  titleKey,
  adviceKey,
  values,
  code,
  icon,
  detail,
  actions = [],
  footer,
  fullscreen = false,
  testId,
}) {
  const { t } = useTranslation();

  return (
    <SystemPage
      tone={tone}
      icon={icon}
      code={code}
      title={titleKey ? t(titleKey, values) : undefined}
      body={adviceKey ? t(adviceKey, values) : undefined}
      detail={detail}
      footer={footer}
      fullscreen={fullscreen}
      testId={testId}
      actions={actions}
    />
  );
}
